import React from 'react'
import styled from '@emotion/styled'
import { fontSizeXSmall, fontSize2XSmall, grayLight20, fontWeightBold } from '../../constants/principles'
import { extractGenericOptions, useGenericOptions } from '../../utils/genericOptionsUtils'
import { formatPrice } from '../../utils/kbbUtils'
import { getFinalSellingSignalRanking } from '../../utils/sellingSignalUtil'
import CounterofferIcon from '../../images/counteroffer.svg'
import AcceptOfferIcon from '../../images/accept-offer.svg'

export default ({ vehicleInfo, offerDetails, isAdmin = false, showContact = false }) => {
  let isOnDetailsPage = offerDetails ? true : false
  const contactInfo = vehicleInfo && vehicleInfo.contactInfo
  const contactData = [
    { id: 'name', text: 'Name', data: contactInfo && contactInfo.firstName + ' ' + contactInfo.lastName },
    { id: 'email', text: 'Email', data: contactInfo && contactInfo.email },
    {
      id: 'phone',
      text: 'Phone',
      data: contactInfo && contactInfo.phone && contactInfo.phone.replace(/^(\+0?1\s)(\d{3})[\s.-](\d{3})[\s.-](\d+)/, '($2) $3-$4')
    },
    { id: 'zip', text: 'Zipcode', data: contactInfo && contactInfo.zip }
  ]
  const vehicleDesc = vehicleInfo && vehicleInfo.vehicle && vehicleInfo.vehicle.vehicleDescription
  const vehicleData = vehicleInfo && [
    { id: 'vin', text: 'VIN', data: vehicleInfo && vehicleInfo.vehicle.vin || vehicleDesc && vehicleDesc.vin },
    { id: 'vehicle', text: 'Vehicle', data: vehicleDesc && vehicleDesc.year && vehicleDesc.year.concat(' ', vehicleDesc.make, ' ', vehicleDesc.model, ' ', vehicleDesc.trim) || vehicleDesc && vehicleDesc.vehicle },
    { id: 'mileage', text: 'Mileage', data: vehicleInfo && vehicleInfo.mileage || vehicleDesc && vehicleDesc.mileage },
    { id: 'color', text: 'Color', data: vehicleDesc && vehicleDesc.color },
    { id: 'keys', text: '2 Sets of Keys', data: vehicleInfo && vehicleInfo.history && vehicleInfo.history.filter(i => i.questionId === 'qp/79')[0].value ? 'Yes' : 'No' || vehicleDesc && vehicleDesc.twoSetsOfKeys }
  ]
  const prospectVehicleOptions = vehicleInfo && vehicleInfo.vehicle && vehicleInfo.vehicle.vehicleOptions
  const vehicleOptions = [
    { id: 'engine', text: 'Engine', data: vehicleDesc && vehicleDesc.engine || prospectVehicleOptions && prospectVehicleOptions.engine },
    { id: 'transmission', text: 'Transmission', data: vehicleDesc && vehicleDesc.transmission || prospectVehicleOptions && prospectVehicleOptions.transmission },
    { id: 'drivetrain', text: 'Drive', data: vehicleDesc && vehicleDesc.drivetrain || prospectVehicleOptions && prospectVehicleOptions.drive }
  ]
  const prospectVehicleHistory = vehicleInfo && vehicleInfo.vehicle && vehicleInfo.vehicle.vehicleHistory
  const offerVehicleHistory = vehicleInfo && vehicleInfo.history
  const vehicleHistory = vehicleInfo && [
    { text: 'Original Owner', value: offerVehicleHistory && vehicleInfo.history.filter(i => i.questionId === 'qp/83')[0].value ? 'Yes' : 'No' || prospectVehicleHistory && prospectVehicleHistory.originalOwner },
    { text: 'Accidents', value: offerVehicleHistory && vehicleInfo.history.filter(i => i.questionId === 'qp/124')[0].value || prospectVehicleHistory && prospectVehicleHistory.accidents },
    { text: 'Auctioned in Last 45 Days', value: offerVehicleHistory && vehicleInfo.history.filter(i => i.questionId === 'qp/81')[0].value ? 'Yes' : 'No' || prospectVehicleHistory && prospectVehicleHistory.auctionedInLast45Days },
    { text: 'Rental Vehicle', value: offerVehicleHistory && vehicleInfo.history.filter(i => i.questionId === 'qp/125')[0].value ? 'Yes' : 'No' || prospectVehicleHistory && prospectVehicleHistory.rentalVehicle }
  ]
  const timeElapsed = Date.now()
  const today = new Date(timeElapsed)
  const offerDetailsObject =
    isOnDetailsPage &&
    [
      { text: 'Original Offer Creation Date', value: offerDetails.originalDateCreated ? offerDetails.originalDateCreated.split('T')[0] : 'N/A' },
      { text: 'Original Offer Expiration Date', value: offerDetails.originalExpirationDate ? offerDetails.originalExpirationDate.split('T')[0] : 'N/A' },
      { text: 'Appointment Requested', value: offerDetails.appointment?.isRequestedAppointment ? offerDetails.appointment.dayOfWeek + ' ' + offerDetails.appointment.timeFrame : 'No' }
    ].concat(
      offerDetails.sellingSignalsEnable
        ? {
            text: 'ICO Selling Signal',
            value: offerDetails.sellingSignalSummary
          }
        : []
    )

  const offerDetailsObjectAmount = isOnDetailsPage && [
    { text: 'Original Offer Amount', value: formatPrice(offerDetails.offerAmount) },
    { text: 'Boost Amount', value: formatPrice(offerDetails.boostAmount) },
    { text: 'Original Offer Amount + Boost Amount', value: formatPrice(offerDetails.offerPlusBoostAmount) }
  ]

  if (isOnDetailsPage && offerDetails.counterofferAmount !== undefined) {
    const isOfferAcceptance = offerDetails.counterofferAmount === 0
    const counterofferDiff = offerDetails.counterofferAmount - offerDetails.offerPlusBoostAmount
    const counterofferSign = counterofferDiff >= 0 ? '+' : '−'
    const counterofferPrice = (
      <>
        <b>{formatPrice(offerDetails.counterofferAmount)}</b> ({counterofferSign}<b>{formatPrice(Math.abs(counterofferDiff))}</b>)
      </>
    )
    const counterofferLine = {
      text: (
        <>
          <CounterofferImage src={isOfferAcceptance ? AcceptOfferIcon : CounterofferIcon} alt={isOfferAcceptance ? 'Offer Acceptance' : 'Counteroffer'} />
          Counteroffer
        </>
      ),
      value: isOfferAcceptance ? 'Consumer Accepted Initial Amount' : counterofferPrice
    }
    offerDetailsObjectAmount.splice(1, 0, counterofferLine) // insert into 2nd line
  }

  // new logic of displaying generic options over installed options
  let installedVehicleOptions = undefined
  const prospectInstalledVehicleOptions = prospectVehicleOptions && vehicleInfo.vehicle.vehicleOptions.installedOptions
  if (prospectInstalledVehicleOptions) {
    installedVehicleOptions = prospectInstalledVehicleOptions.map((opt, i) => <li key={`opt${i}`}>{opt}</li>)
  }
  else if (vehicleInfo && useGenericOptions(vehicleInfo.genericOptions)) {
    //extract generic options
    const extractedGenericOptions = extractGenericOptions(vehicleInfo.genericOptions)
    installedVehicleOptions = extractedGenericOptions && extractedGenericOptions.map((opt, i) => <li key={`opt${i}`}>{opt}</li>)
  }
  // else do nothing
  else {
    installedVehicleOptions = vehicleInfo && vehicleInfo.options && vehicleInfo.options.map(opt => (opt.userIsSelected || opt.isSelected) && <li key={opt.optionId}>{opt.displayName}</li>)
  }

  const prospectReplacementVehicle = vehicleInfo && vehicleInfo.vehicle && vehicleInfo.vehicle.replacementVehicle
  const showPainMeter = false

  // inserts Odors question into condition section
  if (vehicleInfo && vehicleInfo.conditions && !vehicleInfo.conditions.find(q => q.questionId === 'qp/65')) {
    vehicleInfo.conditions.push({
      questionId: 'qp/65',
      item: 'Smoking/Other Odors',
      label: vehicleInfo.history.filter(i => i.questionId === 'qp/65')[0].value ? 'Yes' : 'No',
      section: 'Vehicle Condition',
      subsection: 'Interior Wear or Damage',
      questionType: 'ToggleSelect',
      value: vehicleInfo.history.filter(i => i.questionId === 'qp/65')[0].value ? 'Yes' : 'No',
      acceptsComment: false,
      comment: ''
    })
  }

  const paintMeter =
    vehicleInfo && vehicleInfo.paintMeter && vehicleInfo.paintMeter.length > 0
      ? vehicleInfo.paintMeter
          .map(x => {
            return { questionId: x.questionId, value: x.value, order: x.label.split('|')[0], label: x.label.split('|')[1] }
          })
          .sort((a, b) => a.order - b.order)
      : []
  const uniqueSections = arr => {
    return Array.from(new Set(arr.map(item => item.subsection)))
  }

  const displayResponse = q => {
    const StyledUl = styled.ul`
      margin-top: 0px;
    `

    let data =
      q.questionType === 'ToggleSelect' && q.value ? (
        q.label ? (
          q.acceptsComment && q.comment ? (
            <div key={q.questionId}>
              {q.item}:{' '}
              <StyledUl>
                <li>{q.label}</li>
                {q.acceptsComment && q.comment && (
                  <ul>
                    <li>Explanation: {q.comment}</li>
                  </ul>
                )}
              </StyledUl>
            </div>
          ) : (
            <div key={q.questionId}>
              {q.item}: {q.subsectionCode === 'WarningLights' ? 'Stays On' : q.label}
            </div>
          )
        ) : q.acceptsComment && q.comment ? (
          <div key={q.questionId}>
            <StyledUl>
              <li>{q.item}</li>
              {q.acceptsComment && q.comment && (
                <ul>
                  <li>Explanation: {q.comment}</li>
                </ul>
              )}
            </StyledUl>
          </div>
        ) : (
          <div>{q.item}</div>
        )
      ) : q.questionType === 'SingleSelect' ? (
        q.selectChoices.filter(x => x.selected && x.acceptsComment && x.comment.length > 0).length > 0 ? (
          <div key={q.questionId}>
            {q.label}:{' '}
            <StyledUl>
              <li>{q.selectChoices.filter(x => x.selected)[0].value}</li>
              <ul>
                <li>Explanation: {q.selectChoices.filter(x => x.selected)[0].comment}</li>
              </ul>
            </StyledUl>
          </div>
        ) : (
          <div key={q.questionId}>
            {q.label}: {q.selectChoices.filter(x => x.selected)[0].value}
          </div>
        )
      ) : q.questionType === 'Integer' ? ( //the only integer question with comments is in History, which is already separately handled
        <div>
          {q.label}: {q.value}
        </div>
      ) : q.questionType === 'MultiSelect' ? (
        <div key={q.questionId}>
          {q.itemHeader}:{' '}
          <StyledUl>
            {q.selectChoices
              .filter(x => x.selected)
              .map((i, index) => {
                if (i.acceptsComment && i.comment) {
                  return (
                    <React.Fragment key={index}>
                      <li>{i.value}</li>
                      <ul>
                        <li>Explanation: {i.comment}</li>
                      </ul>
                    </React.Fragment>
                  )
                } else {
                  return <li key={index}>{i.value}</li>
                }
              })}
          </StyledUl>
        </div>
      ) : (
        ''
      )
    return data
  }

  return (
    <>
      {showContact && (
        <InfoSection>
          <InfoSectionHeader>Contact Information</InfoSectionHeader>
          {contactInfo &&
            contactData.map(i => (
              <div key={i.id}>
                <BoldSpan>{i.text}: </BoldSpan>
                {i.data}
              </div>
            ))}
        </InfoSection>
      )}
      {offerDetailsObject && (
        <InfoSection>
          {offerDetails.sellingSignalsEnable && <div>{getFinalSellingSignalRanking(offerDetails.consumerInsights.marketLevel, offerDetails.sellingSignalsWeightingTotal, offerDetails.sellingSignalsEnable)}</div>}
          {offerDetails.offerStatus !== 'INELIGIBLE' && offerDetails.offerStatus !== 'INSPECTION' ? (
            offerDetailsObjectAmount.map((i, index) => (
              <div key={index}>
                <BoldSpan>{i.text}: </BoldSpan>
                {i.value}
              </div>
            ))
          ) : (
            <div>
              <BoldSpan>Offer Status: </BoldSpan>
              {offerDetails.offerStatus}
            </div>
          )}
          {offerDetailsObject.map((i, index) => (
            <div key={index}>
              <BoldSpan>{i.text}: </BoldSpan>
              {i.value}
            </div>
          ))}
          {offerDetails && offerDetails.consumerInsights && (
            <div key="insights">
              <BoldSpan>ICO Buying Signal: </BoldSpan>
              {offerDetails.consumerInsights.marketLevel}, {offerDetails.consumerInsights.PreferredVehicleType} {offerDetails.consumerInsights.makeOfPreferredModel} {offerDetails.consumerInsights.preferredModel} as of {today.toLocaleDateString()}
            </div>
          )}
          {offerDetails.offerImages && offerDetails.offerImages.length > 0 && (
            <DetailsImageContainer>
              {offerDetails.offerImages.map((i, index) => (
                <DetailsImage key={index} src={i.imageUrl} alt={i.category}></DetailsImage>
              ))}
            </DetailsImageContainer>
          )}
        </InfoSection>
      )}
      {isOnDetailsPage && vehicleInfo && vehicleInfo.history && (
        <InfoSection>
          <InfoSectionHeader>Vehicle History</InfoSectionHeader>
          {vehicleHistory.map((i, index) => (
            <div key={index}>
              <BoldSpan>{i.text}: </BoldSpan>
              {i.value}
            </div>
          ))}
        </InfoSection>
      )}
      <InfoSection>
        <InfoSectionHeader>Vehicle Description</InfoSectionHeader>
        {vehicleDesc &&
          vehicleData.map(
            i =>
              i.data && (
                <div key={i.id}>
                  <BoldSpan>{i.text}: </BoldSpan>
                  {i.data}
                </div>
              )
          )}
      </InfoSection>
      <InfoSection>
        <InfoSectionHeader>Vehicle Options</InfoSectionHeader>
        {vehicleOptions.map(
          i =>
            i.data && (
              <div key={i.id}>
                <BoldSpan>{i.text}: </BoldSpan>
                {i.data}
              </div>
            )
        )}
        <div>
          <BoldSpan>Installed Options: </BoldSpan>
          <StyledUl>{installedVehicleOptions}</StyledUl>
        </div>
      </InfoSection>
      <InfoSection>
        <InfoSectionHeader>Vehicle Modifications</InfoSectionHeader>
        {vehicleInfo && vehicleInfo.conditions && vehicleInfo.conditions.filter(item => item.section === 'Aftermarket').length > 0
          ? uniqueSections(vehicleInfo.conditions.filter(item => item.section === 'Aftermarket')).map(subsection => {
            let questions = vehicleInfo.conditions.filter(item => item.section === 'Aftermarket').filter(x => x.subsection === subsection)
            return (
              questions.length > 0 && (
                <React.Fragment key={subsection}>
                  <SubsectionHeader>{subsection}: </SubsectionHeader>
                  {questions.map(q => displayResponse(q))}
                </React.Fragment>
              )
            )
          })
          : 'No vehicle modification information entered'}
      </InfoSection>
      {!isOnDetailsPage && vehicleInfo && (vehicleInfo.history || prospectVehicleHistory) && (
        <InfoSection>
          <InfoSectionHeader>Vehicle History</InfoSectionHeader>
          {vehicleHistory.map((i, index) => (
            <div key={index}>
              <BoldSpan>{i.text}: </BoldSpan>
              {i.value}
            </div>
          ))}
        </InfoSection>
      )}
      <InfoSection>
        <InfoSectionHeader>Condition</InfoSectionHeader>
        {vehicleInfo && vehicleInfo.conditions && vehicleInfo.conditions.filter(item => item.section === 'Vehicle Condition').length > 0
          ? uniqueSections(vehicleInfo.conditions.filter(item => item.section === 'Vehicle Condition')).map((subsection, i) => {
            let questions = vehicleInfo.conditions.filter(item => item.section === 'Vehicle Condition').filter(x => x.subsection === subsection)
            return (
              questions.length > 0 && (
                <React.Fragment key={subsection}>
                  <SubsectionHeader>{subsection}: </SubsectionHeader>
                  {questions.map(q => displayResponse(q))}
                  {i === 0 && subsection === 'Condition Tier Selection' ? <br></br> : ''}
                </React.Fragment>
              )
            )
          })
          : 'No condition information entered'}
      </InfoSection>
      <InfoSection>
        <InfoSectionHeader>Replacement Vehicle</InfoSectionHeader>
        {vehicleInfo &&
          ((vehicleInfo.replacementVehicle && vehicleInfo.replacementVehicle.filter(i => i.questionId === 'qrv/LeadType')[0].value !== 'OptIn') || (prospectReplacementVehicle && prospectReplacementVehicle.lookingFor === '') ? (
            <div>No replacement vehicle entered</div>
          ) : (
            <>
              <div>
                <BoldSpan>Looking For: </BoldSpan>
                {vehicleInfo &&
                  vehicleInfo.replacementVehicle &&
                  vehicleInfo.replacementVehicle
                    .filter(i => i.questionId === 'qrv/LookingFor')[0]
                    .subquestions.filter(i => i.value)
                    .map(i => i.label.split(' ')[0])
                    .join(', ')}
                {prospectReplacementVehicle && prospectReplacementVehicle.lookingFor}
              </div>
              <div>
                <BoldSpan>Vehicle: </BoldSpan>
                {vehicleInfo && vehicleInfo.replacementVehicle && vehicleInfo.replacementVehicle.filter(i => i.questionId === 'qrv/MakeAndModel')[0].subquestions.filter(i => i.questionId === 'qrv/MakeModel1')[0].label}
                {prospectReplacementVehicle && prospectReplacementVehicle.vehicle}
              </div>
              <div>
                <BoldSpan>Year Range: </BoldSpan>
                {vehicleInfo && vehicleInfo.replacementVehicle && vehicleInfo.replacementVehicle.filter(i => i.questionId === 'qrv/YearRange')[0].min + '-' + vehicleInfo.replacementVehicle.filter(i => i.questionId === 'qrv/YearRange')[0].max}
                {prospectReplacementVehicle && prospectReplacementVehicle.year}
              </div>
            </>
          ))}
      </InfoSection>
      {isAdmin && showPainMeter && (
        <InfoSection>
          <InfoSectionHeader>Paint Meter Readings</InfoSectionHeader>
          {paintMeter.length > 0 && paintMeter.some(q => q.value)
            ? paintMeter.map((i, index) => (
                <div key={index}>
                  <BoldSpan>{i.label}: </BoldSpan>
                  {i.value}
                </div>
              ))
            : 'No paint meter readings entered'}
        </InfoSection>
      )}
      {isAdmin && (
        <InfoSection>
          <InfoSectionHeader>Additional Comments</InfoSectionHeader>
          {vehicleInfo && vehicleInfo.dealerComment ? <div key="dealerComment">{vehicleInfo.dealerComment}</div> : 'No additional comments entered'}
        </InfoSection>
      )}
    </>
  )
}

const BoldSpan = styled.span`
  font-weight: ${fontWeightBold};
`
const SubsectionHeader = styled.div`
font-weight:bold;+
`
const InfoSection = styled.div`
  padding: 16px 0px;
  :not(:last-child) {
    border-bottom: 1px solid ${grayLight20};
  }
  text-align: left;
  font-size: ${fontSize2XSmall};
`
const InfoSectionHeader = styled.div`
  font-size: ${fontSizeXSmall};
  font-weight: bold;
  padding-bottom: 4px;
`
const StyledUl = styled.ul`
  column-count: 2;
  column-gap: 16px;
  list-style: none;
  text-align: left;
  padding-left: 2px;
`
const DetailsImageContainer = styled.div`
  display: grid;
  grid-template-columns: 100px 100px 100px;
  grid-template-rows: auto;
  column-gap: 10px;
  row-gap: 10px;
`
const DetailsImage = styled.img`
  width: 100%;
`
const CounterofferImage = styled.img`
  margin-bottom: 4px;
  margin-right: 2px;
`